/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { Trans, useTranslation } from "@i18n";
import { untranslated, type TranslatedString } from "@i18n";
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import axiosWrapper from "@AxiosWrapper";
import { TemplateUrl } from "@Services/dataManager";
import { formCategories } from "@Helpers";
import { RecursiveConfirmationPopup } from "@Features/files";
import { showToastNotification } from "@Reducers/toastSlice";
import { useDispatch } from "react-redux";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import _ from "lodash";
import IconComponent from "@CubeComponents/icons";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import FormTemplatePreviewPopup from "./FormTemplatePreviewPopup";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import Text from "@CubeComponents/html/Text/Text";
import type { StandardResponse } from "@Types/fileOperations";
import type { SetFileModelFormTemplateIdRequest } from "@ServerTypes/fileOperations/fileOperations.types.js";
import type { SetAssetFormTemplateResponse } from "@ServerTypes/assets/assets.schema";
import type { GetFormTemplateResponse, SetFormTemplateResponse } from "@ServerTypes/projects/project.schema";

function FormTemplate({ selectedFile, projectId, resourceType }: any) {
  const resourceTypeText = {
    files: "Files",
    issues: "Issues",
    mailBox: "Mails",
    model: "Models",
    transmittal: "Transmittals",
    assets: "ASSETS",
    assetsInstance: "ASSETS_INSTANCE",
    drawing: "drawing",
  };

  const defaultRecursiveConfirmationPopupInfo = {
    visibilityStatus: "closed",
    radioButtonLabels: [] as string[],
    headerTitle: "",
    additionalTextBeforeOptions: "" as any,
    optionButtonsText: [] as string[],
    action: "update",
    recursiveOverrideWarningMessage: "" as any,
    isRecursive: false,
    potentialFormTemplateOptionId: undefined as any,
    potentialFormTemplateOptionName: undefined as any,
  };

  const defaultSelectedFormTemplateInfo = {
    id: null as any,
    isPreviewModalOpen: false,
    isDisabled: false,
    isVaries: false,
    tooltipText: "",
  };

  const { t } = useTranslation();
  const [selectedFormTemplateInfo, setSelectedFormTemplateInfo] = useState(defaultSelectedFormTemplateInfo);
  const [recursiveConfirmationPopupInfo, setRecursiveConfirmationPopupInfo] = useState(
    defaultRecursiveConfirmationPopupInfo,
  );
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);

  const dispatch = useDispatch();

  const query = {
    resourceType: (formCategories as any)[resourceType],
    isArchived: false,
    params: {
      resourceType: (formCategories as any)[resourceType],
      isArchived: false,
    },
  };

  const init = async () => {
    const result = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplateId`, {
      projectId,
      resourceType: (formCategories as any)[resourceType],
      hierarchyId: selectedFile?.hierarchyId,
    });
    const selectedFormTempId = _.get(result, "data.data.formTemplateId", null);

    setSelectedFormTemplateInfo({
      ...defaultSelectedFormTemplateInfo,
      id: selectedFormTempId,
      isDisabled:
        selectedFile?.resourceType == "RESOURCE_TYPE_MERGED_FILE" ||
        (selectedFile?.hierarchyId && !selectedFile?.permission?.write),
      isVaries: _.get(result, "data.data.isVaries", false),
      tooltipText: _.get(result, "data.data.tooltipText", ""),
    });
    setShowOverrideWarning(!["issues", "model", "files", "assets"].includes(resourceType));
  };

  useEffect(() => {
    if (selectedFile?.modelFormTemplateId) {
      setSelectedFormTemplateInfo({
        ...defaultSelectedFormTemplateInfo,
        id: selectedFile.modelFormTemplateId,
      });
    } else {
      resourceType && projectId && init();
    }
  }, [resourceType, projectId, selectedFile]);

  const textLabel = {
    model: "Model",
    drawing: "Drawing",
    assets: "Assets",
    issues: "Issues",
    mailBox: "Mails",
  };

  const handleTemplateChange = async (e: any) => {
    const potentialFormTemplateOptionId = e._id;
    const potentialFormTemplateOptionName = e.name;
    const recursiveOverrideWarningMessage = (
      <Trans
        i18nKey="override_warning_for_templ_change"
        values={{
          resource_type: (resourceTypeText as any)[resourceType],
        }}
      />
    );
    if (resourceType === "assetsInstance" || resourceType === "assets") {
      if (selectedFormTemplateInfo.id) {
        setRecursiveConfirmationPopupInfo({
          ...defaultRecursiveConfirmationPopupInfo,
          radioButtonLabels: [
            t("Override custom fields and data in existing Assets"),
            t(
              "Retain custom fields and data in existing Assets and new Assets will contain custom fields from the selected template",
            ),
          ],
          headerTitle: t("Change Template"),
          additionalTextBeforeOptions: (
            <>
              {t("Are you sure you want to set")} <strong>{potentialFormTemplateOptionName}</strong>{" "}
              {t("as template for Assets?")}
            </>
          ),
          optionButtonsText: [t("Save"), t("Cancel")],
          visibilityStatus: "open",
          potentialFormTemplateOptionId,
          potentialFormTemplateOptionName,
          recursiveOverrideWarningMessage,
        });
      } else {
        await updateFormTemplate(potentialFormTemplateOptionId, potentialFormTemplateOptionName);
        if (resourceType === "assetsInstance") {
          await axiosWrapper.post<SetAssetFormTemplateResponse>(`${getBackendUrl()}/assets/setAssetFormTemplate`, {
            projectId,
          });
        }
      }
    } else if (["model", "drawing"].includes(resourceType)) {
      if (selectedFormTemplateInfo.id) {
        setRecursiveConfirmationPopupInfo({
          ...defaultRecursiveConfirmationPopupInfo,
          visibilityStatus: "open",
          radioButtonLabels: [t("Yes"), t("No")],
          headerTitle: t("Change Template"),
          additionalTextBeforeOptions: selectedFile
            ? `Do you want to override the ${textLabel[resourceType]} template for this ${textLabel[resourceType]}?`
            : `Do you want to override the ${textLabel[resourceType]} template for all files in this project?`,
          potentialFormTemplateOptionId,
          potentialFormTemplateOptionName,
          recursiveOverrideWarningMessage,
        });
      } else {
        await updateFormTemplate(potentialFormTemplateOptionId, potentialFormTemplateOptionName);
      }
    } else if (resourceType === "files") {
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        visibilityStatus: "open",
        potentialFormTemplateOptionId,
        potentialFormTemplateOptionName,
        recursiveOverrideWarningMessage,
      });
    } else if (resourceType === "issues") {
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        radioButtonLabels: [
          t("Override custom fields and data in existing issues"),
          t(
            "Retain custom fields and data in existing issues and new issues will contain custom fields from the selected template",
          ),
        ],
        headerTitle: t("Set Custom Fields Template"),
        additionalTextBeforeOptions: (
          <Trans
            i18nKey="set_this_custom_template_confirmation_question"
            values={{
              custom_form_template_name: potentialFormTemplateOptionName,
            }}
            components={{ bold: <strong /> }}
          />
        ),
        optionButtonsText: [t("Apply"), t("Cancel")],
        visibilityStatus: "open",
        potentialFormTemplateOptionId,
        potentialFormTemplateOptionName,
        recursiveOverrideWarningMessage,
      });
    } else if (resourceType === "mailBox") {
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        radioButtonLabels: [
          t("Override custom fields and data in existing mails"),
          t(
            "Retain custom fields and data in existing mails and new mails will contain custom fields from the selected template",
          ),
        ],
        headerTitle: t("Set Custom Fields Template"),
        additionalTextBeforeOptions: (
          <Trans
            i18nKey="set_this_custom_template_confirmation_question"
            values={{
              custom_form_template_name: potentialFormTemplateOptionName,
            }}
            components={{ bold: <strong /> }}
          />
        ),
        optionButtonsText: [t("Apply"), t("Cancel")],
        visibilityStatus: "open",
        potentialFormTemplateOptionId,
        potentialFormTemplateOptionName,
        recursiveOverrideWarningMessage,
      });
    } else {
      await updateFormTemplate(potentialFormTemplateOptionId, potentialFormTemplateOptionName);
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        visibilityStatus: "closed",
      });
    }
  };

  const handlePopupClose = () => {
    setRecursiveConfirmationPopupInfo({
      ...defaultRecursiveConfirmationPopupInfo,
      visibilityStatus: "closedFromCancel",
    });
  };

  const toggleIsRecursive = (event: ChangeEvent<HTMLInputElement>) => {
    setRecursiveConfirmationPopupInfo((prev) => ({
      ...prev,
      isRecursive: event.target.value === "overRide",
    }));
  };

  const updateFileModelTemplate = async (templateId: any, templateName: any) => {
    const payload: SetFileModelFormTemplateIdRequest = {
      fileId: selectedFile.hierarchyId,
      templateId,
      projectId,
      isRecursive: recursiveConfirmationPopupInfo.isRecursive,
    };
    const response: { data: StandardResponse } = await axiosWrapper.post<StandardResponse>(
      `${getBackendUrl()}/fileOperations/setFileModelFormTemplateId`,
      payload,
    );
    if (response.data.status) {
      setSelectedFormTemplateInfo({
        ...selectedFormTemplateInfo,
        id: templateId,
      });
      dispatch(
        showToastNotification({
          content: t("success_message_for_template_change", { template_name: templateName }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage
            ? untranslated(response.data.errorMessage)
            : t("Template Change Failed !"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const updateFormTemplate = async (potentialFormTemplateOptionId: any, potentialFormTemplateOptionName: any) => {
    const action = recursiveConfirmationPopupInfo.action;
    const sendObject: any = {
      projectId,
      resourceType: (formCategories as any)[resourceType],
      isRecursive: recursiveConfirmationPopupInfo.isRecursive,
      action,
    };
    if (action !== "reset") {
      sendObject.id = potentialFormTemplateOptionId;
    }
    let formTemplateUpdateResult = await axiosWrapper.post<SetFormTemplateResponse>(
      `${getBackendUrl()}/projects/setFormTemplate`,
      sendObject,
    );
    if (_.get(formTemplateUpdateResult, "data.status")) {
      let successMessage;
      if (action == "update") {
        setSelectedFormTemplateInfo({
          ...defaultSelectedFormTemplateInfo,
          id: potentialFormTemplateOptionId,
        });
        successMessage = t("success_message_for_template_change", { template_name: potentialFormTemplateOptionName });
      } else if (action == "reset") {
        setSelectedFormTemplateInfo(defaultSelectedFormTemplateInfo);
        successMessage = t("success_message_for_template_reset", {
          resource_type: (resourceTypeText as any)[resourceType],
        });
      } else {
        successMessage = t("Template updated successfully");
      }
      dispatch(
        showToastNotification({
          content: successMessage,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      const errorMessage = _.get(
        formTemplateUpdateResult,
        "data.errorMessage",
        t("Template Change Failed !"),
      ) as TranslatedString;
      dispatch(
        showToastNotification({
          content: errorMessage,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleRecursiveConfirmation = async () => {
    if (selectedFile?.hierarchyId) {
      await updateFileModelTemplate(
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionId,
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionName,
      );
    } else {
      await updateFormTemplate(
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionId,
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionName,
      );
      if (resourceType === "assets") {
        await axiosWrapper.post<SetAssetFormTemplateResponse>(`${getBackendUrl()}/assets/setAssetFormTemplate`, {
          projectId,
        });
      }
    }
    setRecursiveConfirmationPopupInfo({
      ...defaultRecursiveConfirmationPopupInfo,
      visibilityStatus: "closedAfterSumbit",
    });
  };

  const handleResetTemplate = async () => {
    const resourceLabel = (textLabel as any)[resourceType];
    setRecursiveConfirmationPopupInfo({
      ...defaultRecursiveConfirmationPopupInfo,
      radioButtonLabels: [
        t("remove_custom_fields_template_reset", { resource_type: resourceLabel.toLowerCase() }),
        t("retain_custom_fields_template_reset", { resource_type: resourceLabel.toLowerCase() }),
      ],
      headerTitle: t("Reset Custom Fields Template"),
      additionalTextBeforeOptions: t("Are you sure you want to reset custom fields Template?"),
      optionButtonsText: [t("Apply"), t("Cancel")],
      visibilityStatus: "open",
      action: "reset",
      recursiveOverrideWarningMessage: (
        <Trans
          i18nKey="override_warning_for_templ_reset"
          values={{
            resource_type: (resourceTypeText as any)[resourceType],
          }}
        />
      ),
    });
  };

  const renderTemplateOption = useCallback(
    (option: any) => (
      <div className="flex items-center gap-2 w-full overflow-hidden">
        <span className="truncate">{option.name}</span>
        {option.isDefault && (
          <span className="rounded-full bg-gray-500 text-white px-2 py-1 shrink-0">{t("Default")}</span>
        )}
      </div>
    ),
    [t],
  );

  const renderDropDown = useCallback(() => {
    const showTooltip = selectedFormTemplateInfo.isVaries;
    const tooltipText = selectedFormTemplateInfo.tooltipText;
    const dropdown = (
      <SelectDropDown
        disabled={selectedFormTemplateInfo.isDisabled}
        remoteDataSource={TemplateUrl}
        query={query}
        searchable
        value={selectedFormTemplateInfo.id}
        fields={{ value: "_id", text: "name" }}
        onChange={handleTemplateChange}
        width={"100%"}
        placeholder={selectedFormTemplateInfo.isVaries ? t("Varies") : t("Select a Template")}
        renderOption={renderTemplateOption}
      />
    );
    return (
      <div className="flex items-center w-full gap-8">
        <label className="font-semibold whitespace-nowrap">
          {selectedFile
            ? resourceType == "model"
              ? t("Model Template")
              : t("Drawing Template")
            : t("Select a Form Template")}
        </label>
        {showTooltip ? <ToolTipComponent content={untranslated(tooltipText)}>{dropdown}</ToolTipComponent> : dropdown}
      </div>
    );
  }, [selectedFile, selectedFormTemplateInfo.id, selectedFormTemplateInfo.isVaries, renderTemplateOption]);

  return (
    <div className={`h-full w-full flex flex-col gap-8 ${selectedFile ? "p-2" : "p-8"}`}>
      {recursiveConfirmationPopupInfo.visibilityStatus === "open" && (
        <RecursiveConfirmationPopup
          handleSubmit={handleRecursiveConfirmation}
          handleClose={handlePopupClose}
          module="project"
          header="form template"
          toggleIsRecursive={toggleIsRecursive}
          radioButtonLabels={recursiveConfirmationPopupInfo.radioButtonLabels as any}
          headerTitle={recursiveConfirmationPopupInfo.headerTitle}
          additionalTextBeforeOptions={recursiveConfirmationPopupInfo.additionalTextBeforeOptions}
          optionButtonsText={recursiveConfirmationPopupInfo.optionButtonsText as any}
          isRecursive={recursiveConfirmationPopupInfo.isRecursive}
          recursiveOverrideWarningMessage={recursiveConfirmationPopupInfo.recursiveOverrideWarningMessage}
          engineType
        />
      )}
      <div className="flex w-full items-center gap-4">
        {renderDropDown()}
        {["assets", "issues", "mailBox"].includes(resourceType) && (
          <div className="flex items-center gap-4">
            {["assets", "issues"].includes(resourceType) && (
              <ButtonComponent onClick={handleResetTemplate} disabled={!selectedFormTemplateInfo.id}>
                <div className="flex items-center gap-2">
                  <IconComponent
                    name="reset_template"
                    width="1rem"
                    height="1rem"
                    isEnabled={!!selectedFormTemplateInfo.id}
                  />
                  <Text>{t("Reset")}</Text>
                </div>
              </ButtonComponent>
            )}
            <ButtonComponent
              disabled={!selectedFormTemplateInfo.id}
              onClick={() => {
                setSelectedFormTemplateInfo((prev) => ({ ...prev, isPreviewModalOpen: true }));
              }}
            >
              <Text>{t("Preview")}</Text>
            </ButtonComponent>
          </div>
        )}
      </div>

      {showOverrideWarning && (
        <p className="text-sm">
          <Text>
            <Trans
              i18nKey="template_change_message"
              values={{
                resource_type: resourceType == "assets" ? "Assets" : (resourceTypeText as any)[resourceType],
              }}
            />
          </Text>
        </p>
      )}
      <FormTemplatePreviewPopup
        isVisible={selectedFormTemplateInfo.isPreviewModalOpen}
        onCancel={() => {
          setSelectedFormTemplateInfo((prev) => ({
            ...prev,
            isPreviewModalOpen: false,
          }));
        }}
        formTemplateId={selectedFormTemplateInfo.id}
        projectId={projectId}
        resourceType={resourceType}
      />
    </div>
  );
}

export default FormTemplate;
